/**
 * L'orario di un appuntamento, lato interfaccia: da quello che arriva dall'API
 * a quello che si mette in un `<input type="time">`, e da lì a come si legge.
 *
 * Speculare a `backend/src/utils/orario.ts`, che fa lo stesso viaggio
 * nell'altra direzione. Le due metà devono restare d'accordo su una cosa sola:
 * l'orario è una stringa `HH:MM`, e non diventa mai un istante.
 *
 * ## La trappola del fuso, vista da qui
 *
 * `orario_inizio`/`orario_fine` sono colonne `TIME`, e Prisma le serializza in
 * JSON come `"1970-01-01T09:30:00.000Z"`: una data finta con dentro l'ora
 * giusta. Passarla a `new Date(...).getHours()` applica il fuso di chi guarda —
 * in Italia d'estate un appuntamento delle 9:30 si leggerebbe alle 11:30.
 *
 * Quindi si affetta la stringa e non si costruisce mai una `Date`. È la stessa
 * disciplina già in vigore sulle date, che si confrontano come `yyyy-MM-dd`
 * (vedi `utils/dataIpotesi.ts`).
 */

/**
 * L'orario come lo vuole un `<input type="time">`: `"09:30"`, oppure `""` se
 * non c'è — il campo vuoto è la normalità, non un errore.
 *
 * Accetta sia la ISO piena che arriva dall'API sia un `HH:MM` già pronto, così
 * rileggere un valore appena digitato non lo rovina.
 */
export function orarioPerInput(valore: string | null | undefined): string {
  if (!valore) return "";
  // Già "HH:MM" (o "HH:MM:SS"): si tengono le prime cinque cifre.
  if (/^\d{2}:\d{2}/.test(valore)) return valore.slice(0, 5);
  // ISO da Prisma: l'ora comincia dopo la "T", e si taglia lì senza convertire.
  const t = valore.indexOf("T");
  return t === -1 ? "" : valore.slice(t + 1, t + 6);
}

/**
 * L'orario di un appuntamento in una riga: `"09:30 – 12:00"`, `"dalle 09:30"`,
 * `"fino alle 12:00"`, o `null` se non c'è niente da dire.
 *
 * Stesse tre forme di `intervalloOrario` nel backend, che è quello che compone
 * il campo `orario` delle schede del Calendario unificato. Qui serve ai moduli
 * che ricevono le due estremità grezze (le Installazioni), perché la stessa
 * scheda non si legga in due modi diversi a seconda della pagina.
 *
 * Le due estremità sono indipendenti — capita di sapere quando si comincia e
 * non quando si finisce — e il testo lo rispecchia invece di inventare
 * l'estremità mancante.
 */
export function intervalloOrario(
  inizio: string | null | undefined,
  fine: string | null | undefined
): string | null {
  const a = orarioPerInput(inizio);
  const b = orarioPerInput(fine);
  if (a && b) return `${a} – ${b}`;
  if (a) return `dalle ${a}`;
  if (b) return `fino alle ${b}`;
  return null;
}
